import {
  createBotConversation,
  createNewConversation,
  createNewConversationWithMeeting,
  getConversationById,
} from 'api/conversation'
import { trackEvent, trackPageEvent } from 'api/events'
import { fetchServerEvaluatedTargetingConditions } from 'api/targeting'
import store from 'config/store'
import { getUnreadCampaignIdsFromStore } from 'modules/campaigns'
import { clearActiveCampaign } from 'modules/campaigns/actions'
import { renderBotCampaign } from 'modules/campaigns/renderer'
import { clearActiveConversation, receiveConversation, setActiveConversation, setHasForcedConversation } from 'modules/conversations/actions'
import {
  receiveEmbedAwayMessage,
  receiveEmbedConfigurationOverride,
  receiveEmbedWelcomeMessage,
} from 'modules/embed/actions'
import { getBotSenderId, getCampaignFromEmbedById, getEmbedLocale } from 'modules/embed/utils'
import { updateIdentity } from 'modules/identity'
import { trackWidgetImpression } from 'modules/impressions/utils'
import { markTime } from 'modules/metrics/utils'
import { collectAndQualify } from 'modules/qualify'
import { countSiteVisit } from 'modules/session'
import {
  receiveContext,
  receiveEndUser,
  receiveExternalJwt,
  receiveUpdatedCampaignRefreshToken,
  updateInstanceId,
  waitForExternalJwt,
} from 'modules/session/actions'
import { getIsOnline,getTeamAvailability } from 'modules/targeting/availability'
import { evaluateCampaignTargetingConditions } from 'modules/targeting/evaluation'
import { evalTargetingAndEnrollment, getTargetingEvaluationContext } from 'modules/targeting/utils'
import { hideWidgetController, showWidgetController } from 'modules/view'
import {
  setCloseChatRedirectUrl,
  toggleChat as toggleChatAction,
  toggleConversationHistory,
  toggleMatchedWidgetTargeting,
} from 'modules/view/actions'
import { shouldWidgetIconDisplay } from 'modules/view/selectors'
import { is, isEmpty, omit, pathOr } from 'ramda'
import { DRIFT_COOKIES } from 'shared/constants/cookies'
import { ConversationId } from 'types/conversation'
import { WidgetImpressionSource } from 'types/targeting'
import { CustomAttributes } from 'types/user'
import { debounce, isNilOrEmpty, log, pause, uuid, warn } from 'utils'
import { fetchWindowContext } from 'utils/context'
import { clearCookies } from 'utils/cookies'
import { requestKillAnnouncementFrames } from 'utils/frames'
import SyncCallbackRunner from 'utils/helpers/SyncCallbackRunner'
import { broadcast } from 'utils/message'
import { sendUsageMetricEvent, timingMetric } from 'utils/metrics'
import { startTraceMark, stopTraceMark } from 'utils/trace'

import { triggerMeetingRequestedAPIEvent, triggerUserAttributesUpdatedEvent } from './events'
import {
  CollectFormDataOptions,
  DriftConfigOptions,
  DriftPublicAPI,
  IdentifyOptions,
  PageTrackEventAttributes,
  StartInteractionParams} from './types'
import { apiError, apiWarning, configOptionsToEmbedConfiguration } from './utils'
import { validateMethod } from './validator'

const DEBOUNCED_API_METHOD_TIME = 500
const debouncedAPIMethod = <F extends (...args: any[]) => any>(method: F) => debounce(method, DEBOUNCED_API_METHOD_TIME, true)

export const config = (configOptions: DriftConfigOptions) => {
  if (!is(Object, configOptions)) { return false }

  const embedConfiguration = configOptionsToEmbedConfiguration(configOptions)

  store.dispatch(
    receiveEmbedConfigurationOverride(embedConfiguration)
  )
}

export const track = (event: string, attributes: CustomAttributes = {}) => {
  try {
    trackEvent(event, attributes)
  } catch (err) {
    warn(["Failed to send track event" + err])
  }
}

export const reset = async () => {
  await clearCookies(DRIFT_COOKIES)

  broadcast({
    topic: 'reload-all-frames',
    message: {}
  })
}

export const toggleChat = () => {
  const chatOpen = pathOr(false, ['view', 'chatOpen'], store.getState())
  chatOpen ? hideChat() : openChat()
}

export const toggleChatTakeoverModal = () => {
  show()
  store.dispatch(toggleChatAction({
    chatOpen: true,
    isChatTakeover: true
  }))
}

export const openChat = () => {
  show()
  store.dispatch(toggleChatAction({ chatOpen: true }))
}

export const hideChat = () => {
  store.dispatch(toggleChatAction({ chatOpen: false }))
}

export const page = async (pageName?: string) => {
  hideChat()

  const windowContext = await fetchWindowContext()
  store.dispatch(receiveContext(windowContext))

  const pageAttributes: PageTrackEventAttributes = {
    url: pathOr('',  ['page', 'href'], windowContext),
    referrer: pathOr('', ['page', 'referrer'], windowContext),
    title: pathOr('', ['page', 'title'], windowContext),
    pageName: pageName || ''
  }

  trackPageEvent(pageAttributes)

  const { data: campaignRefreshToken } = await broadcast({
    topic: 'reset-campaign-refresh-token',
  })

  store.dispatch(updateInstanceId(uuid()))
  store.dispatch(receiveUpdatedCampaignRefreshToken(campaignRefreshToken))

  await countSiteVisit()

  store.dispatch(clearActiveConversation())
  evalTargetingAndEnrollment()
}

export const evaluateCampaignTargeting = () => {
  evalTargetingAndEnrollment()
}

const _startInteraction = async (params: StartInteractionParams) => {
  const { interactionId: id, goToConversation = true } = params || {}
  const locale = getEmbedLocale()
  if (!id && !is(Number, id)) { return }

  await requestKillAnnouncementFrames()
  store.dispatch(toggleChatAction({ chatOpen: false }))
  store.dispatch(setHasForcedConversation())
  store.dispatch(clearActiveConversation())
  store.dispatch(clearActiveCampaign())

  const { data } = await createBotConversation(id, { isTriggeredFromAPI: true, locale, goToConversation })
  const { data: conversation } = await getConversationById(data.conversationId)
  store.dispatch(receiveConversation({ conversation }))
  store.dispatch(setActiveConversation({ conversationId: data.conversationId }))
}
export const startInteraction = debouncedAPIMethod(_startInteraction)


export const goToConversation = (conversationId: ConversationId) => {
  store.dispatch(setActiveConversation({ conversationId }))
  openChat()
}

export const showWelcomeOrAwayMessage = () => {
  store.dispatch(clearActiveConversation())
  openChat()
}

export const goToConversationList = () => {
  const { conversations } = store.getState()
  if (isEmpty(conversations.conversations)) { return }

  openChat()
  store.dispatch(toggleConversationHistory({
    conversationHistoryOpen: true
  }))
}

export const goToNewConversation = async (message: string = '') => {
  const locale = getEmbedLocale()
  try {
    const { data } = await createNewConversation(message, { isTriggeredFromAPI: true, locale })
    const { data: conversation } = await getConversationById(data.conversationId)

    store.dispatch(receiveConversation({ conversation }))
    store.dispatch(setActiveConversation({ conversationId: data.conversationId }))
  } catch (err) {

  }
}

export const show = () => {
  store.dispatch(toggleMatchedWidgetTargeting({
    matchedTargeting: true
  }))
  showWidgetController()
}

export const hide = () => {
  hideChat()
  hideWidgetController()
}

export const toggleHoursAndTargeting = (toggle: boolean = true) => {
  store.dispatch(toggleMatchedWidgetTargeting({
    matchedHours: toggle,
    matchedTargeting: true
  }))
}

export const showAwayMessage = (message?: string) => {
  store.dispatch(toggleMatchedWidgetTargeting({
    matchedHours: false,
    matchedTargeting: true
  }))

  store.dispatch(clearActiveConversation())
  store.dispatch(clearActiveCampaign())

  if (!!message && typeof message === 'string' && !!message.length) {
    store.dispatch(receiveEmbedAwayMessage({
      message
    }))
  }

  showWidgetController()
  store.dispatch(toggleChatAction({ chatOpen: true, displayGreeting: true }))
}

export const showWelcomeMessage = (message?: string) => {
  store.dispatch(toggleMatchedWidgetTargeting({
    matchedHours: true,
    matchedTargeting: true
  }))

  store.dispatch(clearActiveConversation())
  store.dispatch(clearActiveCampaign())

  if (!!message && typeof message === 'string' && !!message.length) {
    store.dispatch(receiveEmbedWelcomeMessage({
      message
    }))
  }

  showWidgetController()
  store.dispatch(toggleChatAction({ chatOpen: true, displayGreeting: true }))
}

export const scheduleMeeting = async (scheduleMeetingWith: number, options?: Record<string, any>) => {
  const endUserEmail = pathOr(null, ['session', 'endUser', 'email'], store.getState())
  const locale = getEmbedLocale()
  openChat()

  try {
    const { data: { conversationId } } = await createNewConversationWithMeeting(endUserEmail, scheduleMeetingWith, locale, options)
    store.dispatch(setActiveConversation({ conversationId }))
    triggerMeetingRequestedAPIEvent(scheduleMeetingWith)
    trackWidgetImpression(WidgetImpressionSource.CALENDAR_DROP)
  } catch (err) {
    log({ data: [err], type: 'warn' })
  }
}

export const hideAwayMessage = () => hideChat()
export const hideWelcomeMessage = () => hideChat()

export const identify = async (userId: string, attributes: CustomAttributes = {}, options: IdentifyOptions = {}) => {
  // TODO: Looks like post-robot sends a null instead of undefined, causing auto values to fail.
  // Will need to extract some other way
  try {
    const identityAttributes = omit(['hasProvidedIdentity'], attributes)
    await updateIdentity(identityAttributes, userId, options)

    if (!!attributes.email) {
      const email = attributes.email as string
      store.dispatch(receiveEndUser({ endUser: { email } }))
    }
  } catch (err) {
    log({ data: [err], type: 'warn' })
  }
}

export const waitForUserJwt = () => {
  try {
    store.dispatch(waitForExternalJwt())
  } catch (err) {
    log({ data: [err], type: 'warn' })
  }
}

export const setUserJwt = (userJwt: string) => {
  try {
    store.dispatch(receiveExternalJwt(userJwt))
  } catch (err) {
    log({ data: [err], type: 'warn' })
  }
}

export const setUserAttributes = async (attributes: CustomAttributes) => {
  const { session } = store.getState()
  const { externalId } = session

  await updateIdentity(attributes, externalId)
  triggerUserAttributesUpdatedEvent()
}

export const collectFormData = async (formData: Record<string, string>, options: CollectFormDataOptions) => {
  const campaignId = options && typeof options.campaignId === 'number' ? options.campaignId : null
  const followupUrl = options && typeof options.followupUrl === 'string' ? options.followupUrl : null

  markTime('post_form_takeover_tti')
  const shouldTriggerCampaign = await collectAndQualify({
    campaignId,
    formData,
    mapFields: !!options.mapFields
  })

  if (shouldTriggerCampaign) {
    startTraceMark('post_form_takeover_enroll')
    const { session: { campaignRefreshToken }, view } = store.getState()

    const campaign = getCampaignFromEmbedById(campaignId)
    const playbookId = pathOr(null, ['attributes', 'playbookId'], campaign)

    if (view.chatOpen) {
      hideChat()
      await pause(400)
    }

    store.dispatch(clearActiveConversation())
    store.dispatch(clearActiveCampaign())

    if (!isNilOrEmpty(followupUrl)) {
      store.dispatch(setCloseChatRedirectUrl(options.followupUrl))
    }

    toggleChatTakeoverModal()

    await renderBotCampaign(campaignId, playbookId, getBotSenderId(), campaignRefreshToken, false)

    const enrollTime = stopTraceMark('post_form_takeover_enroll')
    if (enrollTime) {
      timingMetric('Post Form Takeover Enroll', enrollTime)
    }
    markTime('post_form_takeover_show')
    return
  }

  if (!isNilOrEmpty(followupUrl)) {
    await broadcast({
      topic: 'redirect-to-url',
      message: {
        url: options.followupUrl
      }
    })
  }
}

export const getWidgetReadyPayload = async () => {
  const state = store.getState()

  const payload = {
    widgetVisible: shouldWidgetIconDisplay(state),
    teamAvailability: await getTeamAvailability(),
    isOnline: await getIsOnline(),
    chatOpen: state.view.chatOpen,
    sidebarOpen: state.view.chatOpen
  }


  return {
    data: {...payload},
    ...payload
  }
}

// set in the conductor
export const off = () => { }
export const on = () => { }

// not used or not supported.
export const ping = () => { apiWarning('drift.ping is not supported in this release.') }
export const startVideoGreeting = () => { apiWarning('drift.startVideoGreeting is not supported in this release.') }
export const debug = (namespaceKey: string = '') => {
  switch(namespaceKey) {
    case 'playbooks':
      debugPlaybookTargeting()
      break
    default:
      break
  }
}

const debugPlaybookTargeting = async () => {
  const { embed } = store.getState()
  const { id: embedId } = embed
  const unreadCampaignIds = getUnreadCampaignIdsFromStore()
  const targetingEvaluationContext = getTargetingEvaluationContext()

  const { data } = await fetchServerEvaluatedTargetingConditions({
    embedId,
    ...targetingEvaluationContext,
    followingCampaignIds: unreadCampaignIds,
    excludedCampaignIds: []
  }, true)

  const { TAL } = await evaluateCampaignTargetingConditions({
    conditions: data.campaignTargetingResults})

  // eslint-disable-next-line
  console.log(`PLAYBOOK_TARGETING_RESULTS: ${JSON.stringify(TAL, null, 2)}`)
}

const API: DriftPublicAPI = {
  identify,
  config,
  track,
  reset,
  debug,
  show,
  ping,
  page,
  hide,
  off,
  on,

  collectFormData,
  goToConversation,
  goToConversationList,
  goToNewConversation,
  hideAwayMessage,
  hideChat,
  hideWelcomeMessage,
  openChat,
  scheduleMeeting,
  setUserAttributes,
  showAwayMessage,
  showWelcomeMessage,
  showWelcomeOrAwayMessage,
  startInteraction,
  startVideoGreeting,
  toggleChat,
  toggleChatTakeoverModal,

  evaluateCampaignTargeting,
  toggleHoursAndTargeting,

  waitForUserJwt,
  setUserJwt
}

const _runner = new SyncCallbackRunner()

export const handleApiEvent = ({
  data
}: {
  data: {
    method: string,
    args?: any[]
  }
}) => {
  const { method, args } = data

  if (!API[method]) {
    apiError(`${method}(...) does not exist.`)
    return
  }

  if (!validateMethod(method, args)) {
    return
  }

  _runner.execute(() => {
    sendUsageMetricEvent('API Event Fired', {
      method
    })
    return API[method](...args)
  })
}
